import './styles.css'

import type { GridConfigState } from '@gravitate-js/excalibrr/dist/components/GraviGrid/hooks/useAGGridEvent'
import type { GridApi } from 'ag-grid-community'
import type {
  BaseFieldProps,
  Filter,
} from '@gravitate-js/excalibrr/dist/components/Grid/SearchGridHeader/DynamicFilterForm/types'
import { type MutableRefObject, useEffect, useRef, useState } from 'react'

import { useUserSettings } from '@/hooks/settings'
import { GraviButton, GraviGrid, MaterialIcon, useLocalStorage } from '@gravitate-js/excalibrr'
import { getReportColumnDefs } from './columnDefs'
import { useDeliveryRecon } from '@/api/useDeliveryReconciliation'
import { useDateRangeFilter } from '@/hooks/useDateRangeFilter'
import { DateRangeFilter } from '@/components/_SiteWide/Grid/ActionButtons/DateRangeFilter'
import { ExpandCollapseButtons } from '@/components/_SiteWide/ExpandCollapseButtons'
import { setFiltersFromSwingReport } from './util'
import { ViewHistoryDrawer } from './components/ViewHistoryDrawer'
import type { DeliveryReconDetailRow, DeliveryReconOverviewRow, DeliveryReconTab } from './types'
import { DetailCellRenderer } from './components/DetailCellRenderer'
import { ChangeBar } from './components/ChangeBar'
import { StatusTabs } from './components/StatusTabs'
import { gridViewManager } from '@/utils/grid'
import { useSearchParams } from 'react-router-dom'

export function DeliveryReconciliation() {
  const gridRef = useRef<GridApi>(null)
  const [expanded, setExpanded] = useState(false)
  const { updateSetting } = useUserSettings()
  const [params] = useSearchParams()

  const {
    filters: dayRangeFilters,
    setFilters: setDayRangeFilters,
    dayRangeStorage,
    setDayRange,
  } = useDateRangeFilter('delivery-recon-dates', {
    back: -7,
    ahead: 1,
  })

  const {
    filters: extendedDayRangeFilters,
    setFilters: setExtendedDayRangeFilters,
    dayRangeStorage: extendedDayRangeStorage,
    setDayRange: setExtendedDayRange,
  } = useDateRangeFilter('extended-delivery-recon-dates', {
    back: -30,
    ahead: 1,
  })

  const {
    useDeliveryReconOverview,
    useApprove,
    useUnapprove,
    useMatch,
    useUnmatch,
    useVoid,
    useUnlink,
    useGetHistory,
  } = useDeliveryRecon()

  const { value: filters, setValue: setFilters } = useLocalStorage<Filter>('delivery_recon_server_query_filters', {
    sectors: [],
    cities: [],
    states: [],
    sites: [],
    products: [],
    bol_number: '',
    statuses: [],
  }) as Filter

  const [dateSetting, setDateSetting] = useState('extended')
  const isExtended = dateSetting === 'extended'
  const { data, isFetching, refetch } = useDeliveryReconOverview(
    isExtended ? extendedDayRangeFilters : dayRangeFilters,
    filters
  )

  const [selectedPairing, setSelectedPairing] = useState<DeliveryReconOverviewRow | null>(null)
  const { data: historyData, isFetching: isHistoryFetching } = useGetHistory(selectedPairing?.match_id ?? null)
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false)
  const [selectedRows, setSelectedRows] = useState<DeliveryReconOverviewRow[]>([])
  const [tab, setTab] = useState<DeliveryReconTab>('open')

  async function handleApprove(selected: DeliveryReconOverviewRow[], isApprove?: boolean) {
    isApprove ? await useApprove.mutateAsync(selected) : await useUnapprove.mutateAsync(selected)
    gridRef?.current?.redrawRows()
  }

  async function handleMatch(selected: DeliveryReconOverviewRow[]) {
    await useMatch.mutateAsync(selected)
    gridRef?.current?.redrawRows()
  }

  async function handleUnmatch(selected: DeliveryReconOverviewRow[]) {
    await useUnmatch.mutateAsync(selected)
    gridRef?.current?.redrawRows()
  }

  async function handleVoid(selected: DeliveryReconOverviewRow[]) {
    await useVoid.mutateAsync(selected)
    gridRef?.current?.redrawRows()
    gridRef?.current?.deselectAll()
    setSelectedRows([])
  }

  async function handleUnlink(id: DeliveryReconOverviewRow['match_id'], selected: DeliveryReconDetailRow) {
    await useUnlink.mutateAsync({ id, selected })
    gridRef?.current?.redrawRows()
    gridRef?.current?.deselectAll()
    setSelectedRows([])
  }

  function handleViewHistory(pairing: DeliveryReconOverviewRow) {
    setOpenHistoryDialog(true)
    setSelectedPairing(pairing)
  }

  const columnDefs = getReportColumnDefs({
    onApprove: handleApprove,
    onViewHistory: handleViewHistory,
  })

  useEffect(() => {
    const site = params.getAll('site')
    const date = params.getAll('date')
    if (site?.length && date?.length)
      setFiltersFromSwingReport({ site, date }, isExtended ? setExtendedDayRange : setDayRange, setFilters)
  }, [])

  const rowData =
    data?.rows?.filter(
      (r) => !tab || (tab === 'open' ? ['unmatched', 'matched'].includes(r.status) : r?.status === tab)
    ) ?? []

  return (
    <>
      <StatusTabs
        data={data}
        tab={tab}
        isExtended={isExtended}
        onChange={(tab, setting) => {
          setDateSetting(setting)
          setTab(tab)
        }}
      />
      <GraviGrid
        externalRef={gridRef as MutableRefObject<GridApi>}
        controlBarProps={{
          title: 'Delivery Reconciliation',
          serverParams: data?.params as BaseFieldProps<Filter>[],
          filters,
          setFilters,
          actionButtons: (
            <>
              <GraviButton
                loading={!!data && isFetching}
                className='mr-2'
                onClick={refetch}
                iconPosition='end'
                icon={<MaterialIcon className='pt-1' icon='refresh' size={14} />}
                appearance='outlined'
              >
                Refresh
              </GraviButton>
              {isExtended ? (
                <DateRangeFilter
                  getFromStorage
                  currentFilters={extendedDayRangeFilters}
                  setCurrentFilters={setExtendedDayRangeFilters}
                  dayRangeStorage={extendedDayRangeStorage}
                  setDayRangeStorage={setExtendedDayRange}
                  defaultSliderValues={[-30, 0]}
                />
              ) : (
                <DateRangeFilter
                  getFromStorage
                  currentFilters={dayRangeFilters}
                  setCurrentFilters={setDayRangeFilters}
                  dayRangeStorage={dayRangeStorage}
                  setDayRangeStorage={setDayRange}
                  defaultSliderValues={[-7, 0]}
                />
              )}
              <ExpandCollapseButtons gridAPIRef={gridRef} isExpanded={expanded} setExpanded={setExpanded} />
            </>
          ),
        }}
        columnDefs={columnDefs}
        detailCellRendererParams={{
          unlink: handleUnlink,
        }}
        detailCellRenderer={DetailCellRenderer}
        masterDetail
        agPropOverrides={{
          detailRowAutoHeight: true,
          rowSelection: 'multiple',
          suppressRowGroupHidesColumns: true,
          suppressRowClickSelection: true,
          suppressGroupRowsSticky: true,
          getRowId: (row) => row.data.match_id,
        }}
        rowData={rowData}
        storageKey='delivery-reconciliation'
        loading={isFetching}
        onGridConfigChanged={(config: GridConfigState) => updateSetting('gridConfig::delivery-reconciliation', config)}
        onSelectionChanged={(params) => setSelectedRows(params.api.getSelectedRows())}
        {...gridViewManager('delivery-reconciliation', updateSetting)}
      />
      {!!selectedRows.length && (
        <ChangeBar
          actions={{
            Match: handleMatch,
            Unmatch: handleUnmatch,
            Approve: (items) => handleApprove(items, true),
            Void: handleVoid,
          }}
          selected={selectedRows}
          onCancel={() => {
            gridRef.current?.deselectAll()
            setSelectedRows([])
          }}
        />
      )}
      <ViewHistoryDrawer
        open={openHistoryDialog}
        onClose={() => setOpenHistoryDialog(false)}
        selectedPairing={selectedPairing}
        setSelectedPairing={setSelectedPairing}
        historyData={historyData}
        isHistoryFetching={isHistoryFetching}
      />
    </>
  )
}
